import "react-toastify/dist/ReactToastify.css";

import axios from "axios";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { StringSchema } from "yup";

import FormPageLayout from "./components/form-page-layout";
import { WORK_API_URL } from "./config/enviroments";

const applicationFormsApiRoute = `${WORK_API_URL}/api/application-forms`;

interface MissingField {
  _id: string;
  field: string;
  label: string;
}

interface SubmitFormProps {
  applicationFormId: string;
}

const SubmitForm: FC<SubmitFormProps> = ({ applicationFormId }) => {
  console.log(process.env.NODE_ENV);
  const [missingFields, setMissingFields] = useState<MissingField[]>([]);
  const [applicationFormIdNumber, setApplicationFormIdNumber] = useState<
    number | null
  >(null);
  const [
    sentInviteRegisterInterviewEmail,
    setSentInviteRegisterInterviewEmail,
  ] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");
  const navigate = useNavigate();
  useEffect(() => {
    axios
      .get(
        `${applicationFormsApiRoute}/${applicationFormId}/get-missing-fields`
      )
      .then((response) => {
        // Handle both old format (array) and new format (object with missingFields)
        if (Array.isArray(response.data)) {
          // Old format - backward compatibility
          setMissingFields(response.data);
        } else {
          // New format
          setMissingFields(response.data.missingFields);
          setApplicationFormIdNumber(response.data.applicationFormId);
          setSentInviteRegisterInterviewEmail(
            response.data.sentInviteRegisterInterviewEmail || false
          );
        }
        setErrorText("");
      })
      .catch((error) => {
        const errorMessage = _.get(
          error,
          "response.data.message",
          error.message
        );
        const applicationFormId = _.get(
          error,
          "response.data.applicationFormId",
          null
        );

        // Check if this is actually a success case (no missing fields)
        if (
          error.response?.status === 400 &&
          errorMessage === "Hồ sơ ứng viên đã điền đủ các thông tin cần thiết"
        ) {
          setMissingFields([]);
          setErrorText("");
          setApplicationFormIdNumber(applicationFormId);
          setSentInviteRegisterInterviewEmail(
            _.get(
              error,
              "response.data.sentInviteRegisterInterviewEmail",
              false
            )
          );
          return;
        }

        const headErrMess = "Có lỗi khi lấy thông tin cần bổ sung";
        toast.error(headErrMess);
        setErrorText(
          `${headErrMess}: ${_.get(error, "response.data.message", error.message)}`
        );
        setApplicationFormIdNumber(applicationFormId);
      });
  }, [applicationFormId]);

  const validationSchema = Yup.object().shape(
    missingFields.reduce(
      (schema, field) => {
        schema[field.field] = Yup.string().required("This field is required");
        return schema;
      },
      {} as { [key: string]: StringSchema<string> }
    )
  );

  const handleSubmit = (
    values: { [key: string]: string },
    { setSubmitting }: FormikHelpers<{ [key: string]: string }>
  ) => {
    const payload = missingFields.map((field) => ({
      field: field.field,
      label: field.label,
      value: values[field.field],
    }));

    axios
      .put(
        `${applicationFormsApiRoute}/${applicationFormId}/update-missing-fields`,
        payload
      )
      .then(() => {
        toast.success("Information updated successfully!");
        setErrorText("");
        // Reload the page to show success block
        window.location.reload();
      })
      .catch((error) => {
        const headErrMsg = "Có lỗi khi cập nhật thông tin";
        toast.error(headErrMsg);
        setErrorText(
          `${headErrMsg}: ${_.get(error, "response.data.message", error.message)}`
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <FormPageLayout
      title="Bổ sung thông tin"
      formId={applicationFormId}
      applicationFormIdNumber={applicationFormIdNumber}
      // description="Vui lòng cung cấp các thông tin còn thiếu để hoàn tất hồ sơ ứng tuyển."
    >
      {!missingFields.length && !errorText ? null : (
        <div className="text-center mt-4 text-danger">{errorText}</div>
      )}
      {!missingFields.length && !errorText ? (
        <div className="text-center mt-5">
          <div className="alert alert-success" role="alert">
            <h5 className="alert-heading">Thông tin đã đầy đủ!</h5>
            <p>
              Bạn đã cung cấp đầy đủ thông tin cần thiết cho hồ sơ ứng tuyển.
            </p>
          </div>
          {applicationFormIdNumber && sentInviteRegisterInterviewEmail && (
            <button
              className="btn btn-primary btn-lg mt-3"
              onClick={() =>
                navigate(`/interview/list?formId=${applicationFormId}`)
              }
            >
              <i className="bi bi-calendar-check"></i> Xem lịch đăng ký
            </button>
          )}
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={missingFields.reduce(
            (initialValues, field) => {
              initialValues[field.field] = "";
              return initialValues;
            },
            {} as { [key: string]: string }
          )}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="p-4 border rounded">
              {missingFields.map((field) => (
                <div key={field._id} className="mb-3 row">
                  <label
                    htmlFor={field.field}
                    className="col-sm-2 col-form-label"
                  >
                    {field.label}
                  </label>
                  <div className="col-sm-10">
                    <Field
                      type="text"
                      name={field.field}
                      className="form-control"
                    />
                    <ErrorMessage
                      name={field.field}
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </div>
              ))}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </FormPageLayout>
  );
};

export default SubmitForm;
